import { ErrorBoundary } from 'react-error-boundary';
import { PermissionError, TierError } from '../utils/custom-errors';
import { logClientError } from '../api/users-api';
import posthog from 'posthog-js';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';

export default function SharedErrorBoundary({
  children,
  section,
}: {
  children: React.ReactNode;
  section: string;
}) {
  const logError = (error: Error, uiSection: string) => {
    if (error instanceof PermissionError || error instanceof TierError) {
      return;
    }

    logClientError(error, uiSection, window.location.href);

    if (import.meta.env.MODE !== 'development') {
      posthog.captureException(error, {
        module: 'Shared Error Boundary',
        section: uiSection,
        location: window.location.href,
      });
    }
  };

  return (
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => {
        if (error instanceof PermissionError || error instanceof TierError) {
          return <Alert severity="error">{error.message}</Alert>;
        }

        return (
          <Alert
            severity="error"
            action={
              <Button
                color="error"
                size="small"
                variant="outlined"
                onClick={resetErrorBoundary}
              >
                Try Again
              </Button>
            }
            sx={{ maxWidth: 800 }}
          >
            {process.env.NODE_ENV === 'development'
              ? error.message
              : "Something went wrong! We've logged the issue and will take a look into it."}
          </Alert>
        );
      }}
      onError={(error) => logError(error, section)}
    >
      {children}
    </ErrorBoundary>
  );
}
